import { Button } from "@/components/ui/button";
import { num, rupee, legText } from "@/lib/api";
import { PayoffToggle } from "@/components/PayoffChart";

const Row = ({ k, v, cls = "text-slate-100", id }) => (
  <div className="flex items-baseline justify-between border-b border-slate-800/60 py-1.5 text-xs">
    <span className="text-slate-500">{k}</span>
    <span data-testid={id} className={`num ${cls}`}>{v}</span>
  </div>
);

const NOTES = {
  iron_condor: "Earns time decay while NIFTY stays between the short strikes. Wings cap the loss.",
  short_straddle: "Unlimited risk. One sharp move can wipe out many winning days. The stop is held to your max risk.",
  short_strangle: "Unlimited risk beyond the breakevens. Wider than a straddle, so it wins more often but collects less.",
  long_straddle: "Profits from a big move in either direction. IV often falls after events (vol crush).",
  long_strangle: "Cheaper than a straddle, but needs a bigger move. Best on marked event days.",
  covered_call: "Simulated: 1 lot of NIFTY held as a future, with a weekly OTM call sold against it. Paper only, never auto-executed.",
};

export const StructureCard = ({ sug, kind, label, onTake, busy, force, onForce, risk }) => {
  const has = sug && sug.legs && !sug.error;
  const credit = has && sug.debit < 0;
  return (
    <div className="panel p-4" data-testid="card-structure-suggestion">
      <span className="eyebrow">Suggested {label} · {kind === "covered_call" ? "paper only" : `max risk ${rupee(risk)}`}</span>
      <div className="mt-2 flex gap-1">
        {[["AUTO", "FROM SIGNAL"], ["ENTER", "PREVIEW NOW"]].map(([d, t]) => (
          <button key={d} data-testid={`structure-mode-${d.toLowerCase()}`} onClick={() => onForce(d === "AUTO" ? null : d)}
            className={`rounded px-2 py-1 text-[10px] font-semibold tracking-wider transition-colors ${(force || "AUTO") === d ? "bg-slate-700 text-slate-50" : "text-slate-500 hover:text-slate-300"}`}>{t}</button>
        ))}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-slate-500" data-testid="structure-note">{NOTES[kind]}</p>
      {!has ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="structure-empty">{sug?.error || "Waiting for the entry window. Tap PREVIEW NOW to price the legs."}</p>
      ) : (
        <div className="mt-3 fade-in">
          <div className="font-display text-lg font-semibold text-indigo-300" data-testid="structure-strategy">{sug.strategy}</div>
          <div className="num text-xs text-slate-500">exp {sug.expiry} · {sug.lots} lot × {sug.lot_size} · spot {num(sug.spot)}</div>
          <div className="mt-3">
            {sug.legs.map((l, i) => (
              <Row key={i} k={`${l.side === "BUY" ? "Buy" : "Sell"} ${legText(l).slice(1)}`} v={num(l.entry)} cls={l.side === "BUY" ? "up" : "down"} id={`structure-leg-${i}`} />
            ))}
            <Row k={kind === "covered_call" ? "Call premium received (pts)" : credit ? "Net credit (pts)" : "Net debit (pts)"} v={num(kind === "covered_call" ? sug.credit : Math.abs(sug.debit))} id="structure-net" />
            {sug.stop_value != null && <Row k="Stop / Target position value" v={`${num(sug.stop_value)} / ${num(sug.target_value)}`} id="structure-stop-target" />}
            {sug.planned_risk != null && <Row k="Planned risk" v={rupee(sug.planned_risk)} cls="down" id="structure-planned-risk" />}
            {sug.target_profit != null && <Row k="Target profit" v={rupee(sug.target_profit)} cls="up" id="structure-target-profit" />}
            <Row k="Max profit" v={sug.max_profit == null ? "Unlimited" : rupee(sug.max_profit)} cls="up" id="structure-max-profit" />
            <Row k="Max loss" v={sug.max_loss == null ? (kind === "covered_call" ? "Index falls (stock risk)" : "Unlimited") : rupee(sug.max_loss)} cls="text-slate-400" id="structure-max-loss" />
            <Row k="Breakeven" v={(sug.breakevens || []).map((b) => num(b)).join(" / ")} id="structure-breakeven" />
          </div>
          {sug.stop_capped && <p className="mt-2 text-[11px] leading-snug text-amber-400/90" data-testid="structure-stop-capped">The stop % is tighter than set, because the loss is capped at {rupee(risk)}. A fast gap can still lose more.</p>}
          {kind === "covered_call" && <p className="mt-2 text-[11px] text-slate-500">Held until expiry ({sug.expiry}, {"\u2265"} time exit), then the next week's call is sold.</p>}
          <PayoffToggle trade={sug} spot={sug.spot} id="payoff-structure-suggestion" />
          <Button data-testid="button-execute-structure-paper" disabled={busy} onClick={onTake} className="mt-4 w-full bg-indigo-500 text-white hover:bg-indigo-400">
            {busy ? "Placing…" : "Take as paper trade"}
          </Button>
        </div>
      )}
    </div>
  );
};
